import { Middleware } from '@reduxjs/toolkit';
import { allTenantDependentDataCleared } from '../action/authenticationEvents';

/** The part of an RTK Query slice this middleware needs. Structural so the
 *  real `createApi` results and test doubles both satisfy it. */
export interface ResettableApiSlice {
    util: { resetApiState: () => { type: string } };
}

/**
 * Minimal view of the state read here. Declared locally because `RootState` is
 * derived from the store that consumes this middleware — importing it would be
 * circular.
 *
 * There are TWO tenant values in the store and they are written by different
 * action creators:
 *   - `legacy.authentication.tenant` — set by `TENANT_SELECTED`
 *     (`action/authenticationEvents`). This is the one `tenantSelector` reads
 *     (`selector/authenticationSelector.ts`) and therefore the one that decides
 *     the `X-Tenant` header on every request (`api/index.ts`). Every real
 *     tenant-change call site dispatches this.
 *   - `authentication.tenant` — the RTK slice mirror, written only by App.tsx's
 *     config effect, which for legacy tenants runs behind an awaited dynamic
 *     `import()` and is skipped entirely when the tenant becomes null.
 *
 * Watching only the mirror would tie the reset to a lagging, sometimes-absent
 * signal — so both are compared and either changing triggers the reset.
 */
interface TenantAwareState {
    legacy?: { authentication?: { tenant?: string | null } };
    authentication?: { tenant?: string | null };
}

const readTenants = (state: TenantAwareState): [string | null, string | null] => [
    state.legacy?.authentication?.tenant ?? null,
    state.authentication?.tenant ?? null,
];

/**
 * Builds middleware that clears every tenant-scoped cache whenever the selected
 * tenant changes.
 *
 * Tenant-scoped RTK Query caches key on the request ARG only — the tenant
 * travels as the `X-Tenant` header. A tenant change therefore invalidates
 * nothing by itself: a subscribed query whose arg is unchanged (most are
 * `undefined`) keeps serving the previous tenant's response indefinitely. That
 * is how another tenant's groups reached the promo-code select.
 *
 * It reacts to the tenant VALUE rather than to an action type on purpose: the
 * tenant is set by two different action creators from seven call sites, only
 * one of which used to clear caches.
 *
 * `allTenantDependentDataCleared` covers the legacy saga-backed state (bookings,
 * locations, depots, issue reports) — same leak class, same trigger, previously
 * dispatched from the switcher alone.
 */
export const createTenantCacheResetMiddleware =
    (slices: ResettableApiSlice[]): Middleware =>
        (api) =>
            (next) =>
                (action) => {
                    const [legacyBefore, mirrorBefore] = readTenants(api.getState() as TenantAwareState);
                    const result = next(action);
                    const [legacyAfter, mirrorAfter] = readTenants(api.getState() as TenantAwareState);

                    const legacyChanged = legacyBefore !== legacyAfter;
                    // The mirror is written by App.tsx's config effect AFTER the
                    // legacy value it echoes — i.e. in a LATER dispatch, once
                    // components have re-rendered and tenant-scoped queries are
                    // already in flight. Resetting on that echo wiped those
                    // in-flight cache entries: RTK then dropped their responses
                    // and the severed subscriptions never refired, freezing every
                    // mounted query hook in isLoading (the customers-screen
                    // infinite spinner on hard reload). A mirror write that lands
                    // ON the current legacy value is therefore a sync echo, not a
                    // tenant change — only a mirror value that genuinely DIVERGES
                    // from the header-deciding legacy value still resets.
                    const mirrorChanged =
                        mirrorBefore !== mirrorAfter && mirrorAfter !== legacyAfter;

                    if (legacyChanged || mirrorChanged) {
                        slices.forEach((slice) => api.dispatch(slice.util.resetApiState()));
                        // redux-actions types its result as an interface, which
                        // carries no implicit index signature and so does not
                        // satisfy `UnknownAction`. Spreading yields an
                        // equivalent object literal that does.
                        api.dispatch({ ...allTenantDependentDataCleared({}) });
                    }

                    return result;
                };
